import { Modal } from 'react-bootstrap';

import { onEnter } from '../../utils';
import Button, { BUTTON_SIZE, BUTTON_TYPE, ButtonGroup } from '../generic/Button';
import type { ConfirmDialogProps } from '../../contexts/DialogContext';

import styles from './ConfirmDialog.module.less';

type Props = Omit<ConfirmDialogProps, 'type'> & {
  isConfirmButtonDisabled?: boolean
};

/**
 * Confirm dialog. Replacement for window.confirm.
 *
 * @param {string} title - Alert title.
 * @param {string | React.ReactElement} body - Alert body.
 * @param {() => void} onConfirm - Called when the user clicks confirm.
 * @param {() => void} onClose - Closes the modal.
 * @param {boolean} allowEnterConfirm - Confirms by pressing the "Enter" key.
 * @param {string} [confirmText] - Confirm button text.
 * @param {string} [confirmBtnType] - Confirm button text.
 * @param {string} [cancelText] - Cancel button text.
 */
const ConfirmDialog = ({
  title,
  body,
  onConfirm,
  onClose,
  allowEnterConfirm = false,
  confirmText = 'Confirm',
  confirmBtnType = BUTTON_TYPE.ERROR, // Red makes users pay more attention.
  cancelText = 'Cancel',
  isConfirmButtonDisabled = false,
}: Props) => (
  <Modal
    onHide={onClose}
    className={styles.modalConfirm}
    backdropClassName={styles.modalConfirmBackdrop}
    onKeyDown={allowEnterConfirm ? onEnter(onConfirm) : undefined}
    show
  >
    <Modal.Header closeButton>
      <Modal.Title>
        {title}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {body}
    </Modal.Body>
    <Modal.Footer className={styles.modalFooter}>
      <ButtonGroup>
        <Button
          type="secondary"
          size={BUTTON_SIZE.MEDIUM}
          onClick={onClose}
        >
          {cancelText}
        </Button>
        <Button
          type={confirmBtnType}
          size={BUTTON_SIZE.MEDIUM}
          onClick={onConfirm}
          isDisabled={isConfirmButtonDisabled}
        >
          {confirmText}
        </Button>
      </ButtonGroup>
    </Modal.Footer>
  </Modal>
);

export default ConfirmDialog;
